import { DateTime } from "luxon";
import type { PbjBuildingAuditSummary, PbjHoursByRoleDaily, PbjRoleSummaryRow, PbjStaffingHoursSummary } from "../../../utils/models";
import type { BuildingResult, WithBuilding } from "./buildingQueries";

const HUNDREDTHS = 100;
/** CMS takes a quarter's PBJ file until 45 days after the quarter ends. */
const CMS_SUBMISSION_DAYS_AFTER_QUARTER = 45;

/** Server hours come rounded to the hundredth; adding them keeps the same precision, without float noise. */
const sumHours = (values: readonly number[]): number => Math.round(values.reduce((total, value) => total + value, 0) * HUNDREDTHS) / HUNDREDTHS;

/** The results for the Building filter's selection; an empty selection means every building. */
export const selectedResults = <Data>(results: readonly BuildingResult<Data>[], selected: ReadonlySet<string>): BuildingResult<Data>[] =>
	results.filter(({ departmentId }) => selected.size === 0 || selected.has(departmentId));

/** Same order as the server's job totals: mapped jobs by description then code, the no-job bucket last. */
const compareJobTotals = (first: PbjRoleSummaryRow, second: PbjRoleSummaryRow): number => {
	if ((first.code === null) !== (second.code === null)) return first.code === null ? 1 : -1;
	const byRole = first.role.localeCompare(second.role);
	if (byRole !== 0 || first.code === null || second.code === null) return byRole;
	return first.code.localeCompare(second.code);
};

/** Several buildings' Staffing Hours totals as one strip: every figure and every job's hours added up. */
export const mergeStaffingSummaries = (summaries: readonly PbjStaffingHoursSummary[]): PbjStaffingHoursSummary => {
	const jobs = new Map<string, { role: string; code: string | null; hours: number[] }>();
	for (const job of summaries.flatMap((summary) => summary.jobTotals)) {
		const key = `${job.code}\n${job.role}`;
		const existing = jobs.get(key);
		if (existing) existing.hours.push(job.hours);
		else jobs.set(key, { role: job.role, code: job.code, hours: [job.hours] });
	}
	return {
		jobTotals: [...jobs.values()].map(({ role, code, hours }) => ({ role, code, hours: sumHours(hours) })).sort(compareJobTotals),
		totalHours: sumHours(summaries.map((summary) => summary.totalHours)),
		weekendHours: sumHours(summaries.map((summary) => summary.weekendHours)),
		weekdayHours: sumHours(summaries.map((summary) => summary.weekdayHours)),
		contractHours: sumHours(summaries.map((summary) => summary.contractHours)),
		dstAffectedDays: summaries.reduce((total, summary) => total + summary.dstAffectedDays, 0),
	};
};

/** Several buildings' Hours by Role series as one: each role's hours added per day, role groups joined. */
export const mergeHoursByRole = (series: readonly PbjHoursByRoleDaily[]): PbjHoursByRoleDaily => {
	const hoursByRoleAndDate = new Map<string, Map<string, number[]>>();
	const rolesByGroup = new Map<string, Set<string>>();
	for (const building of series) {
		for (const [role, byDate] of Object.entries(building.hoursByRole)) {
			// DEFAULT: a role, day or group first seen in this building starts empty and collects from here.
			const days = hoursByRoleAndDate.get(role) ?? new Map<string, number[]>();
			hoursByRoleAndDate.set(role, days);
			// DEFAULT: see above.
			for (const [date, hours] of Object.entries(byDate)) days.set(date, [...(days.get(date) ?? []), hours]);
		}
		// DEFAULT: see above.
		for (const [group, roles] of Object.entries(building.roleGroups)) rolesByGroup.set(group, new Set([...(rolesByGroup.get(group) ?? []), ...roles]));
	}
	return {
		hoursByRole: Object.fromEntries(
			[...hoursByRoleAndDate].map(([role, days]) => [role, Object.fromEntries([...days].map(([date, hours]) => [date, sumHours(hours)]))]),
		),
		roleGroups: Object.fromEntries([...rolesByGroup].map(([group, roles]) => [group, [...roles].sort()])),
	};
};

/** The review buckets of the Summary hero, in the order the hero lists them. */
export enum ReviewBucket {
	LongShifts = "long_shifts",
	Unmapped = "unmapped",
	AfterTermination = "after_termination",
	Capped = "capped",
	ShortShifts = "short_shifts",
}

/** A Summary row's figures, for one building or added across several. */
export type BuildingFigures = Omit<PbjBuildingAuditSummary, "departmentId">;

export const REVIEW_BUCKET_HOURS: Readonly<Record<ReviewBucket, (summary: BuildingFigures) => number>> = {
	[ReviewBucket.LongShifts]: (summary) => summary.longShiftHours,
	[ReviewBucket.Unmapped]: (summary) => summary.unmappedHours,
	[ReviewBucket.AfterTermination]: (summary) => summary.afterTerminationHours,
	[ReviewBucket.Capped]: (summary) => summary.cappedHoursRemoved,
	[ReviewBucket.ShortShifts]: (summary) => summary.shortShiftHours,
};

/** Hours a reviewer should look at before filing. The buckets can overlap, so this can exceed a single bucket's share. */
export const hoursToReview = (summary: BuildingFigures): number => sumHours(Object.values(REVIEW_BUCKET_HOURS).map((hoursOf) => hoursOf(summary)));

/** The building's biggest review bucket — where "Where to start" sends a reviewer. */
export const largestReviewBucket = (summary: BuildingFigures): ReviewBucket =>
	Object.values(ReviewBucket).reduce((largest, bucket) => (REVIEW_BUCKET_HOURS[bucket](summary) > REVIEW_BUCKET_HOURS[largest](summary) ? bucket : largest));

/** Share of hours with nothing to review, as a fraction; overlapping buckets can't push it below 0. */
export const cleanShare = (totalHours: number, reviewHours: number): number | null => (totalHours > 0 ? Math.max(0, 1 - reviewHours / totalHours) : null);

/** Every count and hour figure added across buildings, for the Summary tiles and hero. */
export const addBuildingSummaries = (summaries: readonly BuildingFigures[]): BuildingFigures => ({
	totalHours: sumHours(summaries.map((summary) => summary.totalHours)),
	employeeDays: summaries.reduce((total, summary) => total + summary.employeeDays, 0),
	longShiftCount: summaries.reduce((total, summary) => total + summary.longShiftCount, 0),
	longShiftHours: sumHours(summaries.map((summary) => summary.longShiftHours)),
	shortShiftCount: summaries.reduce((total, summary) => total + summary.shortShiftCount, 0),
	shortShiftHours: sumHours(summaries.map((summary) => summary.shortShiftHours)),
	missingPunchCount: summaries.reduce((total, summary) => total + summary.missingPunchCount, 0),
	breaksRemovedCount: summaries.reduce((total, summary) => total + summary.breaksRemovedCount, 0),
	terminatedCount: summaries.reduce((total, summary) => total + summary.terminatedCount, 0),
	afterTerminationHours: sumHours(summaries.map((summary) => summary.afterTerminationHours)),
	excludedCount: summaries.reduce((total, summary) => total + summary.excludedCount, 0),
	unmappedHours: sumHours(summaries.map((summary) => summary.unmappedHours)),
	unmappedJobCount: summaries.reduce((total, summary) => total + summary.unmappedJobCount, 0),
	cappedHoursRemoved: sumHours(summaries.map((summary) => summary.cappedHoursRemoved)),
});

/** The CMS deadline for the quarter `periodEnd` falls in, as a day in `zone`: the quarter's last day plus 45 days. */
export const cmsDeadline = (periodEnd: string, zone: string): DateTime => {
	const end = DateTime.fromISO(periodEnd, { zone });
	if (!end.isValid) throw new Error(`PBJ period end is not a date: ${periodEnd}`);
	return end.endOf("quarter").startOf("day").plus({ days: CMS_SUBMISSION_DAYS_AFTER_QUARTER });
};

/** Every building's rows in one list, each tagged with its building. */
export const tagRows = <Row>(results: readonly BuildingResult<readonly Row[]>[], buildingName: (departmentId: string) => string): WithBuilding<Row>[] =>
	results.flatMap(({ departmentId, data }) => {
		const name = buildingName(departmentId);
		return data.map((row) => ({ ...row, buildingId: departmentId, buildingName: name }));
	});
